import type { Session } from '@supabase/supabase-js'
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { errorMessage } from './errors'
import { supabase } from './supabase'

export type StaffRole = 'admin' | 'support'

interface AuthState {
  loading: boolean
  session: Session | null
  roles: string[]
  name: string | null
  error: string | null
  isAdmin: boolean
  isStaff: boolean
  /** 'aal2' once the TOTP second factor was verified in this session. */
  aal: string | null
  refreshAal(): Promise<void>
  signIn(email: string, password: string): Promise<void>
  signOut(): Promise<void>
}

const Ctx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [roles, setRoles] = useState<string[]>([])
  const [name, setName] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [aal, setAal] = useState<string | null>(null)

  async function refreshAal() {
    const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
    setAal(data?.currentLevel ?? null)
  }

  async function loadContext(s: Session | null) {
    setSession(s)
    if (!s) {
      setRoles([])
      setName(null)
      setLoading(false)
      return
    }
    await refreshAal()
    const { data, error: e } = await supabase.rpc('get_my_context')
    if (e) {
      setError(errorMessage(e))
      setRoles([])
    } else {
      setRoles(data.roles ?? [])
      setName(data.profile?.full_name ?? s.user.email ?? null)
      setError(null)
    }
    setLoading(false)
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => loadContext(data.session))
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === 'SIGNED_OUT' || event === 'SIGNED_IN' || event === 'USER_UPDATED' || event === 'MFA_CHALLENGE_VERIFIED') void loadContext(s)
      else setSession(s)
    })
    return () => sub.subscription.unsubscribe()
  }, [])

  const value: AuthState = {
    loading,
    session,
    roles,
    name,
    error,
    isAdmin: roles.includes('admin'),
    isStaff: roles.includes('admin') || roles.includes('support'),
    aal,
    refreshAal,
    async signIn(email, password) {
      setError(null)
      const { error: e } = await supabase.auth.signInWithPassword({ email, password })
      if (e) throw new Error(e.message === 'Invalid login credentials' ? 'Correo o contraseña incorrectos.' : e.message)
    },
    async signOut() {
      await supabase.auth.signOut()
    },
  }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth fuera de AuthProvider')
  return v
}
